/**
 * Amazon UK Associates product recommendations.
 * Affiliate tag: integratedsocial-21
 * All links point to amazon.co.uk for UK commission attribution.
 */

export interface AmazonProduct {
  asin: string;
  title: string;
  description: string;
  rating?: number;
  ratingCount?: string;
  price?: string;
  imageUrl?: string;
  category:
    | "ai-books"
    | "marketing-books"
    | "tech-gadgets"
    | "privacy-tools"
    | "car-gadgets"
    | "business-strategy";
  /** Which blog post slugs should show this product */
  relevantSlugs: string[];
}

export type AmazonArticleContext = {
  slug: string;
  tags?: string[];
  title?: string;
  content?: string;
};

const AFFILIATE_TAG = "integratedsocial-21";

export function getAmazonUrl(asin: string): string {
  return `https://www.amazon.co.uk/dp/${asin}?tag=${AFFILIATE_TAG}`;
}

const CONTEXTUAL_PRODUCT_CATEGORIES = {
  privacy: ["privacy-tools", "ai-books", "business-strategy"],
  driving: ["car-gadgets", "ai-books", "business-strategy"],
  creator: ["tech-gadgets", "ai-books", "marketing-books"],
  marketing: ["marketing-books", "ai-books", "business-strategy"],
  general: ["ai-books", "business-strategy", "marketing-books"],
} as const;

function getContextualCategories(
  context: AmazonArticleContext
): readonly AmazonProduct["category"][] {
  const contentText = [context.tags?.join(" "), context.title, context.content]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    /(privacy|security|surveillance|data protection|data governance|cyber)/.test(
      contentText
    )
  ) {
    return CONTEXTUAL_PRODUCT_CATEGORIES.privacy;
  }
  if (/(autonomous|driving|robotaxi|vehicle|car|mobility)/.test(contentText)) {
    return CONTEXTUAL_PRODUCT_CATEGORIES.driving;
  }
  if (
    /(creator|linkedin|thought leadership|content marketing|podcast|webinar|video production)/.test(
      contentText
    )
  ) {
    return CONTEXTUAL_PRODUCT_CATEGORIES.creator;
  }
  if (
    /(marketing|ppc|advertising|performance|max|seo|aeo|geo|lead generation|attribution|campaign)/.test(
      contentText
    )
  ) {
    return CONTEXTUAL_PRODUCT_CATEGORIES.marketing;
  }
  if (
    /(artificial intelligence|\bai\b|agentic|llm|chatgpt|openai|machine learning|automation)/.test(
      contentText
    )
  ) {
    return CONTEXTUAL_PRODUCT_CATEGORIES.general;
  }
  return [];
}

/**
 * Returns no more than three editorially relevant Amazon UK products. Explicit
 * slug assignments retain priority; tag-based fallbacks fill genuine archive
 * gaps without requiring every new post to duplicate product IDs manually.
 */
/**
 * Theme pages that must not carry generic affiliate boxes. Their editorial
 * intent is commercial evaluation, safety reporting or evidence-led agency
 * selection, where unrelated product cards would weaken topical trust.
 */
export const AFFILIATE_EXCLUDED_THEME_SLUGS = new Set([
  "expedia-ai-disintermediation-agentic-commerce-2026",
  "openai-model-misalignment-reporting-framework-2026-09-16",
  "aeo-agency-pricing-uk-cost-roi-guide-2026",
  "aeo-agency-vs-in-house-vs-hybrid-b2b-decision-guide-2026",
  "ai-answers-agency-uk-medium-business",
  "ai-marketing-agency-credibility-test-2026",
  "humain-nvidia-84mw-ai-compute-plan-saudi-arabia-2026",
  "saudi-ai-giga-campus-humain-nebius-datavalt-compute-economics-2026",
]);

export function getProductsForArticle(
  context: AmazonArticleContext
): AmazonProduct[] {
  if (AFFILIATE_EXCLUDED_THEME_SLUGS.has(context.slug)) return [];
  const explicitProducts = amazonProducts.filter(product =>
    product.relevantSlugs.includes(context.slug)
  );
  // Two or more editorially selected resources are sufficient; do not fill a
  // third slot with a broad keyword fallback that may be less relevant.
  if (explicitProducts.length >= 2) return explicitProducts.slice(0, 3);

  const categories = getContextualCategories(context);
  const contextualProducts = categories.flatMap(category =>
    // Products without a reviewed image are deliberately eligible only through
    // their explicit slug assignments, never as a generic fallback elsewhere.
    amazonProducts.filter(
      product => product.category === category && Boolean(product.imageUrl)
    )
  );

  return [...explicitProducts, ...contextualProducts]
    .filter(
      (product, index, all) =>
        all.findIndex(candidate => candidate.asin === product.asin) === index
    )
    .slice(0, 3);
}

/** @deprecated Prefer getProductsForArticle so published content informs matching. */
export function getProductsForSlug(
  slug: string,
  tags: string[] = []
): AmazonProduct[] {
  return getProductsForArticle({ slug, tags });
}

export const amazonProducts: AmazonProduct[] = [
  // Verified editorial resources supplied for the September 2026 Radar series.
  // No price, rating, stock status or product image is asserted without a
  // current product-page review.
  {
    asin: "B0CH1N9MVN",
    title: "The Art of SEO",
    description:
      "A practical reference for teams maintaining technical, content and information-architecture foundations alongside AI-search experimentation.",
    category: "marketing-books",
    relevantSlugs: [
      "human-in-the-loop-wrong-way-to-govern-ai-agents-2026",
      "buyer-agent-merchant-agent-ecommerce-2026",
      "will-ai-kill-saas-or-the-interface-2026",
      "aeo-agency-what-to-hire-for-2026",
      "abm-agency-london-what-good-looks-like",
      "abm-for-fintech-and-financial-services",
      "uk-ai-marketing-agency-who-does-the-work-who-signs",
      "is-aeo-a-new-channel-or-one-seo-job",
      "ai-visibility-check-vs-vanity-score-uk-b2b",
      "what-uk-b2b-companies-should-buy-from-an-ai-marketing-agency",
      "organic-rank-is-not-ai-citation",
      "first-90-days-uk-aeo-programme-what-you-get",
      "is-universal-geo-strategy-dead-engine-specific-authority",
    ],
  },
  {
    asin: "B0BSN6MMTM",
    title: "The Tech SEO Guide",
    description:
      "A technical reference for teams aligning durable site architecture and machine-readable content with evolving search interfaces.",
    category: "marketing-books",
    relevantSlugs: [
      "human-in-the-loop-wrong-way-to-govern-ai-agents-2026",
      "buyer-agent-merchant-agent-ecommerce-2026",
      "will-ai-kill-saas-or-the-interface-2026",
      "aeo-agency-what-to-hire-for-2026",
      "abm-agency-london-what-good-looks-like",
      "abm-for-fintech-and-financial-services",
      "uk-ai-marketing-agency-who-does-the-work-who-signs",
      "is-aeo-a-new-channel-or-one-seo-job",
      "ai-visibility-check-vs-vanity-score-uk-b2b",
      "what-uk-b2b-companies-should-buy-from-an-ai-marketing-agency",
      "organic-rank-is-not-ai-citation",
      "first-90-days-uk-aeo-programme-what-you-get",
      "is-universal-geo-strategy-dead-engine-specific-authority",
    ],
  },
  // ─── AI & Marketing Books ───────────────────────────────────────────
  {
    asin: "1398614297",
    title: "The AI Marketing Canvas",
    description:
      "A five-stage framework for using AI in marketing strategy, personalisation, and customer acquisition.",
    rating: 4.5,
    ratingCount: "280+",
    price: "£22.99",
    imageUrl: "/media/amazon-product-1398614297_001779e8.jpg",
    category: "ai-books",
    relevantSlugs: [
      "chatgpt-modes-explained-work-codex-remote-cloud-guide-2026",
      "prompt-engineering-guide-frameworks-ai-tools-2026",
      "ai-execution-layer-agentic-gtm-buyer-readiness-2026",
      "chatgpt-work-agentic-marketing-workflow-2026",
      "gpt-5-6-cost-per-workflow-ai-metric-2026",
      "openai-smb-advertising-machine-chatgpt-google-ads-2026",
      "chatgpt-ads-performance-media-platform-b2b-2026",
      "ai-search-vendor-research-budget-shift-fractl-survey-2026",
      "should-you-bid-on-your-own-brand-brand-ppc-incrementality-2026",
      "chatgpt-ads-automated-bidding-view-through-attribution-2026",
      "ai-b2b-buyer-verification-layer-vendor-transparency-2026",
      "chatgpt-ads-vs-agentic-demand-attribution-2026",
    ],
  },
  {
    asin: "1781338353",
    title: "AI for Business: A Practical Guide",
    description:
      "How business leaders can extract real value from artificial intelligence - from strategy to implementation.",
    rating: 4.4,
    ratingCount: "190+",
    price: "£16.99",
    imageUrl: "/media/amazon-product-1781338353_7b43e94e.jpg",
    category: "ai-books",
    relevantSlugs: [
      "ai-singularity-consciousness-capability-controversial-2026",
      "waymo-robotaxi-20-million-rides-autonomous-driving-safety-2026",
      "anthropic-multiagent-governance-conflict-enterprise-2026",
      "stripe-openrouter-8-billion-ai-model-routing-economics-2026",
      "agent-advertising-attribution-wall-agentic-ads-measurement-2026",
      "aeo-benchmark-authority-oligopoly-ai-search-visibility-2026",
      "ai-search-vendor-research-budget-shift-fractl-survey-2026",
      "chatgpt-ads-automated-bidding-view-through-attribution-2026",
      "ai-b2b-buyer-verification-layer-vendor-transparency-2026",
      "ai-agent-contextual-authority-governance-security-2026",
      "databricks-enterprise-context-moat-ai-value-layer-2026",
      "databricks-agent-context-moat-enterprise-ai-infrastructure-2026",
      "videoamp-agentic-martech-restructure-saas-handoffs-2026",
      "why-enterprise-ai-agents-fail-workflow-context-2026",
      "openai-multi-agent-hugging-face-governance-2026",
      "salesforce-q2-fy27-agentforce-data-context-workflow-moat-2026",
      "brightedge-agent-edge-second-web-ai-agent-visitors-2026",
      "openai-agi-2026-marketing-ai-discovery-strategy-guide",
      "is-zero-risk-ai-zero-value-consequence-budgets",      "spacex-nvidia-ai-infrastructure-financing-2026",
      "ast-spacemobile-bluebird-satellite-analyst-rating-2026",
      "aurora-autonomous-trucking-fmcsa-warning-beacon-exemption-2026",
      "nvidia-open-ai-factory-standards-ocp-2026",

    ],
  },
  {
    asin: "1529146526",
    title: "The Coming Wave - Mustafa Suleyman",
    description:
      "The co-founder of DeepMind on how AI and synthetic biology will reshape civilisation - and how we can contain it.",
    rating: 4.6,
    ratingCount: "4,800+",
    price: "£9.99",
    imageUrl: "/media/amazon-product-1529146526_c1d27914.jpg",
    category: "ai-books",
    relevantSlugs: [
      "ai-singularity-consciousness-capability-controversial-2026",
      "waymo-robotaxi-20-million-rides-autonomous-driving-safety-2026",
      "anthropic-multiagent-governance-conflict-enterprise-2026",
      "stripe-openrouter-8-billion-ai-model-routing-economics-2026",
      "agent-advertising-attribution-wall-agentic-ads-measurement-2026",
      "aeo-benchmark-authority-oligopoly-ai-search-visibility-2026",
      "agent-killability-enterprise-runtime-containment-2026",
      "ai-agent-contextual-authority-governance-security-2026",
      "google-deepmind-operating-model-commercialisation-pressure-2026",
      "flock-cameras-ai-surveillance-police-abuse-privacy-2026",
      "openai-multi-agent-hugging-face-governance-2026",
      "openai-gpt-6-1-astra-safety-governance-2026",
      "openai-70bn-revenue-run-rate-ipo-economics-2026",
      "instinct-personal-agent-trust-alignment-2026",
      "cue-by-manus-personal-agent-team-governance-2026",
      "is-zero-risk-ai-zero-value-consequence-budgets",      "spacex-nvidia-ai-infrastructure-financing-2026",
      "ast-spacemobile-bluebird-satellite-analyst-rating-2026",
      "aurora-autonomous-trucking-fmcsa-warning-beacon-exemption-2026",
      "nvidia-open-ai-factory-standards-ocp-2026",

    ],
  },
  {
    asin: "1847943276",
    title: "Nexus - Yuval Noah Harari",
    description:
      "From the author of Sapiens: how information networks from Stone Age to AI have shaped - and threatened - human civilisation.",
    rating: 4.4,
    ratingCount: "3,200+",
    price: "£11.99",
    imageUrl: "/media/amazon-product-1847943276_128d0e03.jpg",
    category: "ai-books",
    relevantSlugs: [
      "ai-singularity-consciousness-capability-controversial-2026",
      "waymo-robotaxi-20-million-rides-autonomous-driving-safety-2026",
      "anthropic-multiagent-governance-conflict-enterprise-2026",
      "stripe-openrouter-8-billion-ai-model-routing-economics-2026",
      "agent-advertising-attribution-wall-agentic-ads-measurement-2026",
      "aeo-benchmark-authority-oligopoly-ai-search-visibility-2026",
      "flock-cameras-ai-surveillance-police-abuse-privacy-2026",
      "ai-agent-contextual-authority-governance-security-2026",
      "apple-china-ai-model-global-geo-fragmentation-2026",
      "openai-multi-agent-hugging-face-governance-2026",
      "openai-agi-2026-marketing-ai-discovery-strategy-guide",
      "creator-first-aeo-creator-corroboration-ai-search-2026",
      "brightedge-agent-edge-second-web-ai-agent-visitors-2026",
      "openai-gpt-6-1-astra-safety-governance-2026",
      "openai-70bn-revenue-run-rate-ipo-economics-2026",
      "instinct-personal-agent-trust-alignment-2026",
      "cue-by-manus-personal-agent-team-governance-2026",
    ],
  },
  // ─── Business Strategy Books ────────────────────────────────────────
  {
    asin: "1633697940",
    title: "Competing in the Age of AI",
    description:
      "Harvard Business School professors explain how AI transforms operating models, network effects, and competitive strategy.",
    rating: 4.5,
    ratingCount: "520+",
    price: "£22.00",
    imageUrl: "/media/amazon-product-1633697940_ac8aab5a.jpg",
    category: "business-strategy",
    relevantSlugs: [
      "wpp-job-cuts-ai-advertising-agencies",
      "tencent-ai-advertising-revenue-compute-economics-2026",
      "gemini-37-flash-agentic-price-war-cost-per-workflow-2026",
      "geo-incrementality-attribution-crisis-cmo-measurement-2026",
      "ai-search-vendor-research-budget-shift-fractl-survey-2026",
      "chatgpt-ads-automated-bidding-view-through-attribution-2026",
      "ai-b2b-buyer-verification-layer-vendor-transparency-2026",
      "should-you-bid-on-your-own-brand-brand-ppc-incrementality-2026",
      "creator-first-aeo-creator-corroboration-ai-search-2026",
      "salesforce-q2-fy27-agentforce-data-context-workflow-moat-2026",
      "brightedge-agent-edge-second-web-ai-agent-visitors-2026",
      "openai-agi-2026-marketing-ai-discovery-strategy-guide",
      "chatgpt-ads-vs-agentic-demand-attribution-2026",
    ],
  },
  {
    asin: "0241513545",
    title: "The Invisible Hand of AI - McKinsey",
    description:
      "McKinsey's analysis of how AI is reshaping industries, workforce dynamics, and the global economy through 2030.",
    rating: 4.3,
    ratingCount: "150+",
    price: "£14.99",
    imageUrl: "/media/amazon-product-1633697940_ac8aab5a.jpg",
    category: "business-strategy",
    relevantSlugs: [
      "wpp-job-cuts-ai-advertising-agencies",
      "nielsen-doubleverify-acquisition-independent-measurement-2026",
      "videoamp-agentic-martech-restructure-saas-handoffs-2026",
      "should-you-bid-on-your-own-brand-brand-ppc-incrementality-2026",
      "creator-first-aeo-creator-corroboration-ai-search-2026",
      "salesforce-q2-fy27-agentforce-data-context-workflow-moat-2026",
    ],
  },
  // ─── Tech Gadgets ──────────────────────────────────────────────────
  {
    asin: "B0D7MHFJ2M",
    title: "Logitech Brio 4K Webcam",
    description:
      "4K Ultra HD webcam with HDR, auto light correction, and noise-cancelling mics. Perfect for remote AI team meetings.",
    rating: 4.5,
    ratingCount: "12,400+",
    price: "£169.99",
    imageUrl: "/media/amazon-product-logitech-brio_9e3ee7a8.png",
    category: "tech-gadgets",
    relevantSlugs: [
      "chatgpt-modes-explained-work-codex-remote-cloud-guide-2026",
      "prompt-engineering-guide-frameworks-ai-tools-2026",
      "linkedin-ai-slop-crackdown-authentic-thought-leadership-2026",
    ],
  },
  {
    asin: "B09XS7JWHH",
    title: "Elgato Wave:3 USB Microphone",
    description:
      "Premium USB condenser mic with digital mixing, anti-clipping, and broadcast-quality sound for content creators.",
    rating: 4.6,
    ratingCount: "8,900+",
    price: "£129.99",
    imageUrl: "/media/amazon-product-B09XS7JWHH_fd68832d.jpg",
    category: "tech-gadgets",
    relevantSlugs: [
      "chatgpt-modes-explained-work-codex-remote-cloud-guide-2026",
      "prompt-engineering-guide-frameworks-ai-tools-2026",
      "linkedin-ai-slop-crackdown-authentic-thought-leadership-2026",
      "zoom-geo-source-portfolio-management-ai-search-2026",
    ],
  },
  // ─── Privacy & Security Tools ──────────────────────────────────────
  {
    asin: "B0BHTD6GQN",
    title: "Faraday Bag for Car Keys (2-Pack)",
    description:
      "Signal-blocking pouch that prevents relay attacks on keyless cars. RFID, NFC, WiFi, GPS, and Bluetooth shielded.",
    rating: 4.4,
    ratingCount: "6,200+",
    price: "£9.99",
    imageUrl: "/media/amazon-product-faraday-bag_75e36d07.png",
    category: "privacy-tools",
    relevantSlugs: ["flock-cameras-ai-surveillance-police-abuse-privacy-2026"],
  },
  {
    asin: "B07Q5JHBQJ",
    title: "Webcam Cover Slide (6-Pack)",
    description:
      "Ultra-thin 0.7mm laptop camera covers. Compatible with MacBook, iPad, and all laptops. Protects privacy without residue.",
    rating: 4.6,
    ratingCount: "42,000+",
    price: "£5.99",
    imageUrl: "/media/amazon-product-faraday-bag_75e36d07.png",
    category: "privacy-tools",
    relevantSlugs: ["flock-cameras-ai-surveillance-police-abuse-privacy-2026"],
  },
  {
    asin: "B0CP26NJHM",
    title: "NordVPN Gift Card - 1 Year",
    description:
      "Top-rated VPN for privacy, streaming, and secure browsing. 5,500+ servers in 60 countries. Works on all devices.",
    rating: 4.3,
    ratingCount: "1,800+",
    price: "£49.99",
    imageUrl: "/media/amazon-product-logitech-brio_9e3ee7a8.png",
    category: "privacy-tools",
    relevantSlugs: ["flock-cameras-ai-surveillance-police-abuse-privacy-2026"],
  },
  // ─── Car Gadgets ───────────────────────────────────────────────────
  {
    asin: "B0BXMZJXZK",
    title: "Nextbase 622GW 4K Dash Cam",
    description:
      "4K front-facing dash cam with Alexa, GPS, emergency SOS, and what3words location. Night vision and image stabilisation.",
    rating: 4.4,
    ratingCount: "3,400+",
    price: "£249.99",
    imageUrl: "/media/amazon-product-nextbase-622gw_5e34cda0.jpg",
    category: "car-gadgets",
    relevantSlugs: ["flock-cameras-ai-surveillance-police-abuse-privacy-2026"],
  },
  {
    asin: "B0C1GFXZFQ",
    title: "VIOFO A229 Pro Duo Dash Cam",
    description:
      "Dual-channel 4K+2K dash cam with starlight night vision, GPS, WiFi, and parking mode. 24/7 surveillance protection.",
    rating: 4.5,
    ratingCount: "2,100+",
    price: "£279.99",
    imageUrl: "/media/amazon-product-nextbase-622gw_5e34cda0.jpg",
    category: "car-gadgets",
    relevantSlugs: ["flock-cameras-ai-surveillance-police-abuse-privacy-2026"],
  },
];
